import { WizBaseComponent } from '@wizeoo/akm-component-lib';
import { WizBaseLayout } from '@wizeoo/akm-component-lib';
import { FeatureVO } from '../../vo/core/feature.vo';
import { ProfileVO } from '../../vo/user/profile.vo';
import { RoleVO } from '../../vo/user/role.vo';
/**
 * Enumération des accès possible aux features.
 *
 * CRUD + readonly
 */
export enum PermissionAccess {
  /**
   * Droit de création
   */
  CREATE = 'create',
  /**
   * Droit de lecture
   */
  READ = 'read',
  /**
   * Droit de mise à jour
   */
  UPDATE = 'update',
  /**
   * Droit de suppression
   */
  DELETE = 'delete',
  /**
   * Droit batch
   */
  BATCH = 'batch',
  /**
   * Accès en lecture uniquement
   */
  READONLY = 'readonly',
  /**
   * Aucun accès
   */
  DO_NONE = 'none'
}

/**
 * Droit définissant la visibilité de la feature suivant les accès possible à la feature
 */
export enum PermissionVisibility {
  /**
   * L'élément est caché à l'écran, l'utilisateur ne voit pas la feature
   */
  HIDE = 'hide',
  /**
   * La feature est visible mais n'est pas accessible. Elle est grisée et ne réagit pas au actions.
   * Le rendu peut également être différent dans certains suivant l'implémentation souhaitée
   * (Par exemple la cellule dans le grid n'est pas éditable)
   */
  DISABLED = 'disabled',
  /**
   * La feature est visible mais ne réagit pas aux actions utilisateurs
   * TODO: mode à valider
   */
  SHOW = 'show',
  /**
   * Le résultat du role est ignorer et la feature reste pleinement accessible.
   * TODO: mode à valider
   */
  IGNORE = 'ignore',
}

/**
 * Définit les propriétés permettant d'identifier une permission.
 */
export type Permission = {
  /**
   * La feature ciblée par la permission
   */
  feature: string;
  /**
   * Les accès à vérifier
   */
  accesses: PermissionAccess[];
  /**
   * La visibilité à appliquer à la feature en cas de permission rejetée
   */
  visibility: PermissionVisibility;
};

export type ComponentPermission = {
  component: WizBaseComponent | WizBaseLayout;
  visibility: PermissionVisibility;
  accesses: PermissionAccess[];
};

/**
 * Objet permettant de définir la liste des composants par accessibilité
 */
export type ViewPermission = {
  feature: string;
  permissions: ComponentPermission[];
};

/**
 * Enumérateur des features statiques disponibles dans l'application
 */
export enum StaticFeatureEnum {
  DASHBOARD_DATA = 'DASHBOARD_DATA', // toujours dispo, écran Main
  COUNTRY = 'COUNTRY', // Feature des pays
  LANGUAGE = 'LANGUAGE', // Feature des langues
  BU = 'BU', // Feature des business units
  CHRONOCODE = 'CHRONOCODE', // Feature des chrono codes
  ROLE = 'ROLE', // Feature des roles
  PROFILE = 'PROFILE', // Feature des profils
  USER = 'USER', // Feature des users
  VISION = 'VISION', // Feature des visions
  VISION_GENERATE = 'VISION_GENERATE', // vue de génération des lignes de vision
  FREEDATA = 'FREEDATA', // Feature static des codifications
  MEASURE = 'MEASURE', // Feature static des mesures
  CONNECT = 'CONNECT', // lien vers des fonctions connect
  TEMPLATE = 'TEMPLATING', // Feature static du templating
  INCEXC = 'INCEXC', // gestion des includes / excludes
  STYLE = 'STYLE', // gestion des styles de cellules
  FORMAT = 'DATAFORMAT', // gestion des format de mesures
  CSSRENDER = 'CSSRENDER', // gestion des rendus css 
  WORKFLOW = 'WORKFLOW', // gestion des workflows
  DOM_ADMIN = 'DOM_ADMIN', // admin de domaine
  BU_ADMIN = 'BU_ADMIN', // Feature bu admin
  VISION_ACCESS = 'VISION_ACCESS', // Feature d'accès à la vision
  APP_ADMIN = 'APP_ADMIN', // Administrateur d'application
  BU_VISION_ADMIN = 'BU_VISION_ADMIN', // Administrateur de vision périmètre BU
  DOM_VISION_ADMIN = 'DOM_VISION_ADMIN', // Administrateur de vision périmètre DOM
  VISION_USER = 'VISION_USER',
  VISION_PROFILE = 'VISION_PROFILE',
  VISION_ROLE = 'VISIION_ROLE',
  WG = 'WG', // Feature static de gestion des grilles de poids
  SCENARIO = 'SCENARIO', // Feature static de gestion des scénarios
  MATRIX = 'MATRIX', // Gestion des matrices de données
}

/** rendu composite pour roles / profiles, pour affectation des droits */
export interface FeaturePermission {
  // reprise de l'objet FeatureVO
  chronoCode?: string;
  code?: string;
  dynamic?: boolean | null;
  kind?: string;
  perimeter?: string;
  visionCode?: string;
  businessUnitCode?: string;
  deprecated?: boolean | null;
  domain?: string;
  hasChange?: string;
  id?: number | null;
  maintenanceFlag?: string;
  operation?: string;
  shortCode?: string;
  source?: string;
  uid?: string;
  updateBy?: string;
  updateTime?: number | null;
  version?: number | null;

  feature?: FeatureVO;

  trsCode?: string; // code traduit propre
  trsKind?: string; // traduction du type (profil / role) + détail
  trsLabel?: string; // libellé traduit
  
  profile?: ProfileVO; // profil source
  roles?: RoleVO[]; // collection de rôles (en cas de profil ?)

  tags?: object; // les clés métiers

  doCreate?: boolean; // droit d'accès
  doUpdate?: boolean; // droit d'accès
  doDelete?: boolean; // droit d'accès
  doRead?: boolean; // droit d'accès
  doBatch?: boolean; // droit d'accès

  doProfile?: boolean; // c'est un profil, pas un rôle
}
